import { c as _c } from "react/compiler-runtime";
import './item-card.scss';
import type { PropsWithChildren, ReactNode } from 'react';
import { Skeleton } from '../../atoms/skeleton/skeleton.tsx';
import { cn } from '../../utils/helpers.ts';
export type ItemCardProps = PropsWithChildren & {
  className?: string;
  color?: 'negative' | 'warning';
};

/**
 * Item Card
 */
export const ItemCard: React.FC<ItemCardProps> = t0 => {
  const $ = _c(6);
  const {
    color,
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== color) {
    const colorClass = color ? {
      warning: "ItemCard--warning",
      negative: "ItemCard--negative"
    }[color] : "";
    t1 = cn(["ItemCard rounded-md", colorClass, className]);
    $[0] = className;
    $[1] = color;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const ItemCardSection: React.FC<PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="ItemCard__section py-comfy-sm md:py-comfy-lg pb-comfy-xs px-comfy-sm md:pb-comfy-md md:px-comfy-lg">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/**
 * Item Card Header
 */
export const ItemCardHeader: React.FC<{
  title?: string;
  className?: string;
  badge?: ReactNode;
}> = t0 => {
  const $ = _c(9);
  const {
    title,
    className: t1,
    badge
  } = t0;
  const className = t1 === undefined ? "" : t1;
  let t2;
  if ($[0] !== badge || $[1] !== className) {
    t2 = cn(["ItemCard__header flex justify-between items-center", className], {
      "mb-comfy-sm md:mb-comfy-md": !badge,
      "mb-comfy-xs md:mb-comfy-sm": !!badge
    });
    $[0] = badge;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== title) {
    t3 = title && <h3 className="label-neutral-amplified label-potent md:label-potent label-md md:label-lg">{title}</h3>;
    $[3] = title;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== badge || $[6] !== t2 || $[7] !== t3) {
    t4 = <div className={t2}>{t3}{badge}</div>;
    $[5] = badge;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};

/**
 * Item Card Body - if you need full control
 */
export const ItemCardBody: React.FC<PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="ItemCard__body grid gap-snug-xl md:gap-comfy-sm items-center">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const ItemCardSummary: React.FC<{
  icon?: ReactNode;
  title?: string;
  subTitle?: string;
  isWarning?: boolean;
  isError?: boolean;
  isSubtleTitle?: boolean;
}> = t0 => {
  const $ = _c(9);
  const {
    icon,
    title,
    subTitle,
    isWarning: t1,
    isError: t2,
    isSubtleTitle: t3
  } = t0;
  const isWarning = t1 === undefined ? false : t1;
  const isError = t2 === undefined ? false : t2;
  const isSubtleTitle = t3 === undefined ? false : t3;
  if (!title && !subTitle) {
    return;
  }
  let t4;
  if ($[0] !== isError || $[1] !== isSubtleTitle || $[2] !== isWarning || $[3] !== subTitle || $[4] !== title) {
    t4 = (title || subTitle) && <div className="ItemCard__titlegroup">{title && <div className={cn("ItemCard__title label-md md:label-lg mb-snug-md", {
        "label-potent md:label-potent label-neutral-amplified": !isSubtleTitle,
        "label-neutral-potent": isSubtleTitle
      })}>{title}</div>}{subTitle && <div className={cn("label-neutral-mild label-sm md:label-md", {
        "label-warning-moderate": isWarning,
        "label-negative-moderate": isError
      })}>{subTitle}</div>}</div>;
    $[0] = isError;
    $[1] = isSubtleTitle;
    $[2] = isWarning;
    $[3] = subTitle;
    $[4] = title;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== icon || $[7] !== t4) {
    t5 = <>{icon}{t4}</>;
    $[6] = icon;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};

/**
 * Item Card Meta
 *
 * NOTE Organizes right-aligned text with an optional badge.
 */
export const ItemCardMeta: React.FC<{
  text?: string;
  textContrast?: string;
  badge?: ReactNode;
}> = t0 => {
  const $ = _c(6);
  const {
    text,
    textContrast,
    badge
  } = t0;
  let t1;
  if ($[0] !== text || $[1] !== textContrast) {
    t1 = (text || textContrast) && <span className="label-md md:label-lg"><span className="label-neutral-amplified">{text}</span>{textContrast && <span className="label-neutral-mild">{textContrast}</span>}</span>;
    $[0] = text;
    $[1] = textContrast;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== badge || $[4] !== t1) {
    t2 = <div className="flex flex-col gap-snug-md md:gap-snug-xl items-end text-right">{t1}{badge}</div>;
    $[3] = badge;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const ItemCardDivider = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="ItemCard__divider" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

/**
 * Item Card Footer
 *
 * NOTE that this should almost always take in one or more client components.
 */
export const ItemCardFooter: React.FC<PropsWithChildren<{
  justify?: 'end' | 'between';
}>> = t0 => {
  const $ = _c(6);
  const {
    children,
    justify: t1
  } = t0;
  const justify = t1 === undefined ? "between" : t1;
  const t2 = justify === "between";
  const t3 = justify === "end";
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = cn(["ItemCard__footer flex", {
      "justify-between": t2,
      "justify-end": t3
    }]);
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== children || $[4] !== t4) {
    t5 = <ItemCardSection><div className={t4}>{children}</div></ItemCardSection>;
    $[3] = children;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};
export const ItemCardSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton height={180} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};